<template>
  <div
    class="base-tag"
    :class="[`base-tag--${preset}`, `base-tag--${tone}`, { 'base-tag--skeleton': skeleton }]"
    :style="customSurfaceStyle"
    :aria-busy="skeleton || undefined"
    :aria-hidden="skeleton || undefined"
  >
    <template v-if="!skeleton">
      <BaseIcon
        v-for="(iconName, index) in resolvedIcons"
        :key="`${iconName}-${index}`"
        :name="iconName"
        :color="tagIconColor"
        class="base-tag__icon"
      />
      <span class="base-tag__label">
        <slot />
      </span>
      <slot name="append" />
    </template>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import { isTone, isPreset } from '@/util/design-tokens';

export default {
  name: 'BaseTag',
  components: {
    BaseIcon,
  },
  props: {
    icon: {
      type: String,
      default: null,
    },
    /** Multiple leading icons; takes precedence over `icon` when non-empty. */
    icons: {
      type: Array,
      default: null,
    },
    tone: {
      type: String,
      default: 'neutral',
      validator: isTone,
    },
    preset: {
      type: String,
      default: 'soft',
      validator: isPreset,
    },
    /** Semantic preset: neutral, error, success, disabled. Takes precedence over legacy color styling when set. */
    variant: {
      type: String,
      default: '',
      validator: (v) => !v || VARIANTS.includes(v),
    },
    /** Any valid CSS background value, e.g. #336699 or linear-gradient(90deg, …). Overrides preset/legacy background. */
    background: {
      type: String,
      default: '',
    },
    /** Text/icon color when using a custom background (optional; defaults to white for contrast). */
    textColor: {
      type: String,
      default: '',
    },
    /** Compact shimmer placeholder — same shape as a tag, no label. */
    skeleton: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // Same design token as label text; pass to BaseIcon so stroke/fill using currentColor match the tag.
    tagIconColor() {
      return 'var(--action-text)';
    },
    resolvedIcons() {
      if (this.icons && this.icons.length) return this.icons;
      return this.icon ? [this.icon] : [];
    },
    customSurfaceStyle() {
      if (!this.background) return undefined;
      const textColor = this.textColor || contrastTextForBackground(this.background);
      return {
        '--action-bg': this.background,
        '--action-text': textColor,
        '--action-border': this.background,
      };
    },
  },
};

const contrastTextForBackground = (background) => {
  const hex = String(background || '')
    .replace('#', '')
    .replace(/[^0-9a-f]/gi, '');

  let rgb = '';
  if (hex.length >= 6) rgb = hex.substring(0, 6);
  else if (hex.length === 3)
    rgb = hex
      .split('')
      .map((c) => c + c)
      .join('');
  if (rgb.length !== 6) return '#ffffff';

  const r = parseInt(rgb.substring(0, 2), 16);
  const g = parseInt(rgb.substring(2, 4), 16);
  const b = parseInt(rgb.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness < 128 ? '#ffffff' : '#0f172a';
};
</script>

<style lang="scss" scoped>
.base-tag {
  display: inline-flex;
  width: max-content;
  align-items: center;
  gap: map-get(map-get($aloha-sizes, 'xs'), 'gap');
  box-sizing: border-box;
  min-height: map-get(map-get($aloha-sizes, 'xs'), 'height');
  padding: map-get(map-get($aloha-sizes, 'xs'), 'py') map-get(map-get($aloha-sizes, 'xs'), 'px-round');
  border-radius: $radius-2xl;
  font-family: $font-family-body;
  font-weight: $font-weight-medium;
  font-size: map-get(map-get($aloha-sizes, 'sm'), 'font-size');
  line-height: 1.2;
  // `background` (not `background-color`) so the AI tone's gradient fill is valid.
  background: var(--action-bg);
  color: var(--action-text);
  border: 1px solid var(--action-border);
  transition: background $duration-fast $ease-ui-interaction, border-color $duration-fast $ease-ui-interaction;

  &__icon {
    flex-shrink: 0;
    width: map-get(map-get($aloha-sizes, 'xs'), 'icon-size');
    height: map-get(map-get($aloha-sizes, 'xs'), 'icon-size');
  }

  &__label {
    min-width: 0;
  }

  @each $preset in $aloha-presets {
    @each $tone, $_ in $aloha-tones {
      &--#{$preset}.base-tag--#{$tone} {
        @include action-preset-hooks($preset, $tone);
      }
    }
  }

  &--skeleton {
    width: 40px;
    min-width: 40px;
    pointer-events: none;
    overflow: hidden;
    position: relative;
    background-color: $color-slate-200;
    border-color: transparent;
    color: transparent;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, transparent 0%, rgba($color-bg, 0.7) 50%, transparent 100%);
      animation: base-tag-shimmer $duration-deliberate ease-in-out infinite;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;

    &--skeleton::after {
      animation-duration: 0ms;
    }
  }
}

@keyframes base-tag-shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
